/**
 * Wire-mapping for the read-only evidence records/packages (OIDCPAM Gate-6
 * Phase 6d — AuditEvidence.tsx / EvidenceView.tsx -> @devnw/cyber-iam's
 * iam/EvidencePackageViewer + iam/EvidenceCaseFile).
 *
 * `accessRequestsClient.ts`'s `evidence()` / `evidencePackage()` already run
 * their responses through `deepSnake()` (see that file's doc comment), so
 * the real runtime shape at this boundary IS the hand-written snake_case
 * `EvidenceRecord` / `EvidencePackage` family in ../api/types.ts — this is
 * the same "clean" wire-mapping class as accessRequestMapping.ts's
 * toIamAccessRequest (snake_case -> camelCase field rename only; no
 * snake/camel confusion bug to fix here).
 *
 * The one HOST-COMPUTED value is `AuditSeal.sealedAt`: the wire's
 * `sealed_at` is a raw unix-SECONDS number (AuditSeal.sealed_at in
 * ../api/types.ts), while the library's `AuditSeal.sealedAt` is a
 * pre-formatted display string (the library never parses dates), so this
 * file formats it. The pre-migration AuditEvidence page never rendered
 * `sealed_at` at all — passing a formatted value through now is a strict
 * improvement, not a behavior change a user would perceive as a regression.
 */
import type {
  Approval as IamApproval,
  ActivatedGrant as IamActivatedGrant,
  EvidenceRecord as IamEvidenceRecord,
  EvidencePackage as IamEvidencePackage,
  ChainOfCustodyEntry as IamChainOfCustodyEntry,
  AuditSeal as IamAuditSeal,
} from '@devnw/cyber-iam/iam/types'
import type {
  Approval as WireApproval,
  ActivatedGrant as WireActivatedGrant,
  EvidenceRecord as WireEvidenceRecord,
  EvidencePackage as WireEvidencePackage,
  ChainOfCustodyEntry as WireChainOfCustodyEntry,
  AuditSeal as WireAuditSeal,
} from './types'
import { toIamAccessRequest } from './accessRequestMapping'

function toIamApproval(a: WireApproval): IamApproval {
  return {
    id: a.id,
    approverId: a.approver_id,
    decision: a.decision,
    rationale: a.rationale,
    createdAt: a.created_at,
  }
}

function toIamActivatedGrant(g: WireActivatedGrant): IamActivatedGrant {
  return {
    id: g.id,
    subjectId: g.subject_id,
    resourceId: g.resource_id,
    permissions: g.permissions,
    createdAt: g.created_at,
    expiresAt: g.expires_at,
  }
}

export function toIamEvidenceRecord(r: WireEvidenceRecord): IamEvidenceRecord {
  return {
    requestId: r.request_id,
    request: toIamAccessRequest(r.request),
    approvals: (r.approvals ?? []).map(toIamApproval),
    activatedGrant: r.activated_grant
      ? toIamActivatedGrant(r.activated_grant)
      : undefined,
    generatedAt: r.generated_at,
  }
}

function toIamChainOfCustodyEntry(
  e: WireChainOfCustodyEntry
): IamChainOfCustodyEntry {
  return {
    seq: e.seq,
    eventCode: e.event_code,
    outcome: e.outcome,
    recordHash: e.record_hash,
    subject: e.subject,
    resource: e.resource,
    prevHash: e.prev_hash || undefined,
  }
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtSealedAt(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString()
}

function toIamAuditSeal(s: WireAuditSeal): IamAuditSeal {
  return {
    batchId: s.batch_id,
    terminalHash: s.terminal_hash,
    recordCount: s.record_count,
    sealedAt: fmtSealedAt(s.sealed_at),
  }
}

export function toIamEvidencePackage(
  p: WireEvidencePackage
): IamEvidencePackage {
  return {
    requestId: p.request_id,
    auditTrail: (p.audit_trail ?? []).map(toIamChainOfCustodyEntry),
    seals: (p.seals ?? []).map(toIamAuditSeal),
    chainValid: p.chain_valid,
    evidenceRecord: p.evidence_record
      ? toIamEvidenceRecord(p.evidence_record)
      : undefined,
    chainIssue: p.chain_issue,
    generatedAt: p.generated_at,
    redacted: p.redacted,
  }
}
